import { Color } from "@repo/app-types/constants/colors";
import {
  checkIsManagerGroup,
  type Group,
  type GroupId,
} from "@repo/app-types/entities/group";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Pill } from "@repo/ui/Pills/pill";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconCopy, IconPencil, IconTrash } from "@tabler/icons-react";
import { type FC, useState } from "react";
import {
  useAllTeammateGroupsQuery,
  useDeleteTeammateGroupMutation,
} from "../../../api/entities/group.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { TeammateGroupDialog } from "./teammateGroupDialog.js";

export const TeammateGroupsSettingsSection: FC = () => {
  const { data: groups, isLoading } = useAllTeammateGroupsQuery();
  const [deleteGroup] = useDeleteTeammateGroupMutation();

  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [groupIdToDelete, setGroupIdToDelete] = useState<GroupId | undefined>(
    undefined,
  );

  const handleCopyGroupId = async (groupId: GroupId) => {
    try {
      await navigator.clipboard.writeText(groupId);
      showToast({
        title: "Group ID copied to clipboard",
        variant: ToastVariant.Success,
      });
    } catch (err) {
      showToast({
        title: "Failed to copy group ID",
        variant: ToastVariant.Error,
      });
    }
  };

  // Empty manager groups are already filtered out server-side.
  const visibleGroups = groups?.toSorted((a, b) => {
    const categoryA = checkIsManagerGroup(a) ? 0 : 1;
    const categoryB = checkIsManagerGroup(b) ? 0 : 1;

    // Manager groups first
    if (categoryA !== categoryB) {
      return categoryA - categoryB;
    }

    // Alphabetical order within the same category
    return a.name.localeCompare(b.name);
  });

  const hasAnyManagerGroups = visibleGroups?.some(checkIsManagerGroup);

  return (
    <>
      <SimpleModal
        isOpen={!!groupIdToDelete}
        onCancel={() => setGroupIdToDelete(undefined)}
        variant={SimpleModalVariant.Warning}
        title="Delete teammate group"
        subtitle="Are you sure you want to delete this teammate group? This action can't be undone."
        confirmButtonText="Delete group"
        onConfirm={() => {
          if (groupIdToDelete) {
            deleteGroup(groupIdToDelete);
          }
          setGroupIdToDelete(undefined);
        }}
      />
      <div className="w-full border-b border-gray-900/10 py-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg leading-6 font-semibold text-foreground">
            Manage teammate groups
          </h2>
        </div>
        <div className="my-4 space-y-4">
          <SettingsTable className="w-auto max-w-full">
            <SettingsTableHeaderSection>
              <tr>
                <SettingsTableHeaderCell className="w-64">
                  Name
                </SettingsTableHeaderCell>
                {hasAnyManagerGroups ? (
                  <SettingsTableHeaderCell className="w-24">
                    Type
                  </SettingsTableHeaderCell>
                ) : null}
                <SettingsTableHeaderCell className="w-24">
                  Members
                </SettingsTableHeaderCell>
              </tr>
            </SettingsTableHeaderSection>
            <SettingsTableBodySection>
              {isLoading ? <TableSkeleton /> : null}
              {visibleGroups?.length === 0 ? (
                <SettingsTableEmptyBodyContent colSpan={2}>
                  No groups have been created yet.
                </SettingsTableEmptyBodyContent>
              ) : null}
              {visibleGroups?.map((group) => (
                <tr key={group.id} className="group relative">
                  <SettingsTableDataCell>
                    <p className="font-semibold">{group.name}</p>
                  </SettingsTableDataCell>
                  {hasAnyManagerGroups ? (
                    <SettingsTableDataCell>
                      <Pill
                        size={Size.SMALL}
                        color={
                          checkIsManagerGroup(group) ? Color.Blue : Color.Gray
                        }
                        label={checkIsManagerGroup(group) ? "Team" : "Custom"}
                      />
                    </SettingsTableDataCell>
                  ) : null}
                  <SettingsTableDataCell>
                    {group.teammate_ids.length}

                    {!checkIsManagerGroup(group) ? (
                      <div className="absolute top-1/2 right-3 hidden -translate-y-1/2 cursor-pointer items-center space-x-2 text-muted-foreground group-hover:flex">
                        <IconButton
                          variant={ButtonVariant.Secondary}
                          icon={<IconCopy className="h-4 w-4" />}
                          accessibleLabel="Copy group ID"
                          onClick={() => void handleCopyGroupId(group.id)}
                        />
                        <Can
                          I={PermissionActions.UPDATE}
                          a={PermissionSubjects.GROUPS}
                        >
                          <IconButton
                            variant={ButtonVariant.Secondary}
                            icon={<IconPencil className="h-4 w-4" />}
                            onClick={() => setSelectedGroup(group)}
                          />
                        </Can>
                        <Can
                          I={PermissionActions.DELETE}
                          a={PermissionSubjects.GROUPS}
                        >
                          <IconButton
                            variant={ButtonVariant.Secondary}
                            icon={<IconTrash className="h-4 w-4" />}
                            onClick={() => setGroupIdToDelete(group.id)}
                          />
                        </Can>
                      </div>
                    ) : null}
                  </SettingsTableDataCell>
                </tr>
              ))}
            </SettingsTableBodySection>
          </SettingsTable>
        </div>
        <Can
          I={PermissionActions.CREATE}
          a={PermissionSubjects.GROUPS}
          passThrough={true}
        >
          {(allowed) => (
            <div className="mt-4">
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => setIsCreating(true)}
                isDisabled={!allowed}
              >
                Create teammate group
              </Button>
            </div>
          )}
        </Can>
      </div>

      <TeammateGroupDialog
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
      />

      <TeammateGroupDialog
        isOpen={!!selectedGroup}
        group={selectedGroup || undefined}
        onClose={() => setSelectedGroup(null)}
      />
    </>
  );
};
